import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Users as UsersIcon, UserPlus, Shield, UserX, UserCheck, Trash2, Ban, Settings, X, CheckCircle, FileText, FolderOpen, Mail, Edit, Key, Copy, RefreshCw, Camera, Trash } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import HelperNote from '../components/HelperNote';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Users() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ 
    email: '', 
    name: '', 
    password: '', 
    role: 'takim_uyesi',
    teamIds: [],
    projectIds: []
  });

  // Reset form to initial state
  const resetInviteForm = () => {
    setInviteData({
      email: '',
      name: '',
      password: '',
      role: 'takim_uyesi',
      teamIds: [],
      projectIds: []
    });
  };
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Admin özellik state'leri
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [editData, setEditData] = useState({ name: '', email: '', role: '' });
  const [passwordMode, setPasswordMode] = useState('auto'); // 'auto' or 'manual'
  const [manualPassword, setManualPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showInvitePassword, setShowInvitePassword] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [userToEditPermissions, setUserToEditPermissions] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    // ⚡ PERFORMANCE: Paralel API çağrıları
    Promise.all([
      fetchUsers(),
      fetchProjects(),
      fetchTeams()
    ]).catch(error => {
      console.error('❌ Veri yükleme hatası:', error);
    });
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const sorted = [...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setUsers(sorted);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users/invite`, inviteData);
      toast.success('Kullanıcı oluşturuldu');
      setShowInviteDialog(false);
      resetInviteForm();
      fetchUsers();
    } catch (error) {
      toast.error('Kullanıcı davet edilemedi: ' + (error.response?.data?.detail || 'Hata'));
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { role: newRole });
      toast.success('Rol güncellendi');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rol güncellenemedi');
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { status: 'suspended' });
      toast.success('Kullanıcı askıya alındı');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { status: 'active' });
      toast.success('Kullanıcı aktif edildi');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };


  // Takım Atamaları modalı kaldırıldı - Kullanıcı Düzenleme modalı aynı işlevi yapıyor

  // toggleTeam ve toggleLeadTeam fonksiyonları kaldırıldı - artık kullanılmıyor


  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success('Kullanıcı silindi');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    }
  };

  // Bulk operations
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      // Only select users that can be deleted (exclude admins and users not owned by project manager)
      setSelectedUsers(users.filter(u => canDeleteUser(u)).map(u => u.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Lütfen en az bir kullanıcı seçin');
      return;
    }

    // Open confirmation dialog
    setShowBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await Promise.all(selectedUsers.map(userId => 
        axios.delete(`${API}/users/${userId}`)
      ));
      toast.success(`${selectedUsers.length} kullanıcı silindi`);
      setSelectedUsers([]);
      setSelectAll(false);
      setShowBulkDeleteDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Toplu silme işlemi başarısız');
      setShowBulkDeleteDialog(false);
    }
  };

  const handleBulkSuspend = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Lütfen en az bir kullanıcı seçin');
      return;
    }

    if (!window.confirm(`${selectedUsers.length} kullanıcıyı askıya almak istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await Promise.all(selectedUsers.map(userId => 
        axios.patch(`${API}/users/${userId}`, { status: 'suspended' })
      ));
      toast.success(`${selectedUsers.length} kullanıcı askıya alındı`);
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast.error('Toplu askıya alma başarısız');
    }
  };

  const handleBulkActivate = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Lütfen en az bir kullanıcı seçin');
      return;
    }

    try {
      await Promise.all(selectedUsers.map(userId => 
        axios.patch(`${API}/users/${userId}`, { status: 'active' })
      ));
      toast.success(`${selectedUsers.length} kullanıcı aktif edildi`);
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast.error('Toplu aktifleştirme başarısız');
    }
  };

  const handleResendVerification = async (email) => {
    try {
      await axios.post(`${API}/resend-verification`, { email });
      toast.success('Doğrulama maili tekrar gönderildi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Doğrulama maili gönderilemedi');
    }
  };

  // Admin: Kullanıcı Düzenleme
  const [editPictureFile, setEditPictureFile] = useState(null);
  const [editPicturePreview, setEditPicturePreview] = useState(null);

  const openEditDialog = (user) => {
    setUserToEdit(user);
    setEditData({
      name: user.name,
      email: user.email,
      role: user.role,
      teamIds: user.teamIds || [],
      projectIds: user.projectIds || []
    });
    setEditPictureFile(null);
    setEditPicturePreview(null);
    setShowEditDialog(true);
  };

  const handleEditUser = async () => {
    if (!editData.name.trim() || !editData.email.trim()) {
      toast.error('Ad ve email boş olamaz');
      return;
    }

    try {
      await axios.patch(`${API}/users/${userToEdit.id}`, editData);

      // Profil resmi seçildiyse yükle
      if (editPictureFile) {
        const formData = new FormData();
        formData.append('file', editPictureFile);
        await axios.post(`${API}/users/${userToEdit.id}/profile-picture`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Kullanıcı başarıyla güncellendi');
      setShowEditDialog(false);
      setUserToEdit(null);
      setEditPictureFile(null);
      setEditPicturePreview(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı güncellenemedi');
    }
  };

  // Admin: Şifre Sıfırlama
  const openResetPasswordDialog = (user) => {
    setUserToResetPassword(user);
    setPasswordMode('auto');
    setManualPassword('');
    setGeneratedPassword('');
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    try {
      const body = passwordMode === 'manual' && manualPassword 
        ? { password: manualPassword } 
        : null;

      await axios.post(`${API}/admin/users/${userToResetPassword.id}/reset-password`, body);
      
      // 🔒 P1-FIX: Şifre artık response'da döndürülmüyor, e-posta ile gönderiliyor
      if (passwordMode === 'manual' && manualPassword) {
        setGeneratedPassword(manualPassword);
      } else {
        setGeneratedPassword('');
      }
      toast.success('Şifre başarıyla sıfırlandı. Yeni şifre kullanıcının e-postasına gönderildi.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre sıfırlanamadı');
    }
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Şifre panoya kopyalandı');
  };

  const closeResetPasswordDialog = () => {
    setShowResetPasswordDialog(false);
    setUserToResetPassword(null);
    setPasswordMode('auto');
    setManualPassword('');
    setGeneratedPassword('');
  };

  // Permission management
  const AVAILABLE_PERMISSIONS = [
    // Görev İşlemleri
    { id: 'can_create_tasks', label: 'Görev Oluşturabilir', description: 'Yeni görev oluşturabilir' },
    { id: 'can_view_all_tasks', label: 'Tüm Görevleri Görüntüleyebilir', description: 'Atanmamış görevleri de görebilir' },
    { id: 'can_edit_tasks', label: 'Görevleri Düzenleyebilir', description: 'Görev bilgilerini değiştirebilir' },
    { id: 'can_delete_tasks', label: 'Görevleri Silebilir', description: 'Görevleri kalıcı olarak silebilir' },
    { id: 'can_move_tasks', label: 'Görevleri Taşıyabilir', description: 'Kanban board\'da görevleri kolonlar arasında taşıyabilir' },
    { id: 'can_assign_tasks', label: 'Görev Atayabilir', description: 'Görevleri kullanıcılara atayabilir' },
    { id: 'can_complete_tasks', label: 'Görevleri Tamamlayabilir', description: 'Görevleri tamamlayabilir' },
    
    // Revizyon
    { id: 'can_send_revision', label: 'Revizyona Gönderebilir', description: 'Görevleri önceki adımlara revize edebilir' },
    { id: 'can_resolve_revision', label: 'Revizyonu Tamamlayabilir', description: 'Revizyon sürecini tamamlayabilir' },
    
    // İletişim
    { id: 'can_comment', label: 'Yorum Yapabilir', description: 'Görevlere yorum ekleyebilir' },
    { id: 'can_upload_files', label: 'Dosya Yükleyebilir', description: 'Görevlere dosya ekleyebilir' },
    { id: 'can_download_files', label: 'Dosya İndirebilir', description: 'Görev dosyalarını indirebilir' },
    { id: 'can_delete_files', label: 'Dosya Silebilir', description: 'Görev dosyalarını silebilir' },
    
    // Yönetim
    { id: 'can_manage_users', label: 'Kullanıcıları Yönetebilir', description: 'Kullanıcı ekleme, düzenleme ve silme' },
    { id: 'can_manage_roles', label: 'Rolleri Yönetebilir', description: 'Rol tanımlamalarını düzenleyebilir' },
    { id: 'can_manage_projects', label: 'Projeleri Yönetebilir', description: 'Proje oluşturma, düzenleme ve silme' },
    { id: 'can_manage_teams', label: 'Takımları Yönetebilir', description: 'Takım oluşturma, düzenleme ve silme' },
    { id: 'can_manage_workflows', label: 'İş Akışlarını Yönetebilir', description: 'Workflow tanımlamalarını düzenleyebilir' },
    { id: 'can_manage_tags', label: 'Etiketleri Yönetebilir', description: 'Tag/etiket oluşturma ve düzenleme' },
    
    // Onay Sistemi
    { id: 'can_approve_requests', label: 'Onay İsteklerini Yönetebilir', description: 'Onay isteklerini onaylayabilir/reddedebilir' },
    { id: 'can_send_for_review', label: 'Kontrol İçin Gönderebilir', description: 'Görevleri kontrole gönderebilir' },
    
    // Raporlama
    { id: 'can_view_reports', label: 'Raporları Görüntüleyebilir', description: 'Rapor sayfasına erişebilir' },
    { id: 'can_view_dashboard', label: 'Dashboard Görüntüleyebilir', description: 'Dashboard sayfasına erişebilir' },
  ];

  // Filter users based on search and filters
  const filteredUsers = users.filter(u => {
    // Search filter (name or email)
    const matchesSearch = !searchTerm || 
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Role filter
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    
    // Status filter
    const matchesStatus = filterStatus === 'all' || (u.status || 'active') === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setFilterRole('all');
    setFilterStatus('all');
  };

  const openPermissionsDialog = (u) => {
    setUserToEditPermissions(u);
    setSelectedPermissions(u.permissions || []);
    setShowPermissionsDialog(true);
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId) 
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleUpdatePermissions = async () => {
    if (!userToEditPermissions) return;
    
    try {
      await axios.patch(`${API}/users/${userToEditPermissions.id}`, { 
        permissions: selectedPermissions 
      });
      toast.success('Yetkiler güncellendi');
      setShowPermissionsDialog(false);
      setUserToEditPermissions(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yetki güncelleme başarısız');
    }
  };

  // Helper function to check if current user can delete a specific user
  const canDeleteUser = (targetUser) => {
    // If no user context, cannot delete
    if (!user) {
      return false;
    }
    
    // Cannot delete yourself
    if (targetUser.id === user.id) {
      return false;
    }
    
    // Admin can delete ANYONE (including other admins), except themselves
    if (user.role === 'admin') {
      return true;
    }
    
    // Project managers can only delete users they created
    if (user.role === 'proje_yoneticisi') {
      return targetUser.createdBy === user.id;
    }
    
    // Other roles with can_manage_users can only delete users they created
    if (user?.permissions?.includes('can_manage_users')) {
      return targetUser.createdBy === user.id;
    }
    
    return false;
  };

  const roleLabels = {
    admin: 'Admin',
    proje_yoneticisi: 'Proje Yöneticisi',
    takim_lideri: 'Takım Lideri',
    takim_uyesi: 'Takım Üyesi'
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-800',
    proje_yoneticisi: 'bg-purple-100 text-purple-800',
    takim_lideri: 'bg-blue-100 text-blue-800',
    takim_uyesi: 'bg-green-100 text-green-800'
  };

  const statusLabels = {
    active: 'Aktif',
    suspended: 'Askıda',
    deleted: 'Silindi'
  };

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-yellow-100 text-yellow-800',
    deleted: 'bg-red-100 text-red-800'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <UsersIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="relative">
              Kullanıcılar
              {/* Kullanıcı sayısı badge */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {filteredUsers.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Kullanıcıları yönetin ve roller atayın
            {filteredUsers.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {filteredUsers.length.toLocaleString('tr-TR')} kullanıcı</span>
            )}
          </p>
        </div>
        {user?.permissions?.includes('can_manage_users') && (
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Button
              variant="outline"
              className="border-cyan-200 text-cyan-700 hover:bg-cyan-50"
              onClick={() => navigate('/roles')}
              data-testid="role-management-button"
            >
              <Shield className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Rol Yönetimi</span>
              <span className="sm:hidden">Roller</span>
            </Button>
            <Dialog open={showInviteDialog} onOpenChange={(open) => {
              setShowInviteDialog(open);
              if (open) resetInviteForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-cyan-600 to-blue-600" data-testid="invite-user-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Kullanıcı Ekle
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              {/* Close Button */}
              <button
                onClick={() => setShowInviteDialog(false)}
                className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
              >
                <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
              </button>
              
              <DialogHeader className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 -m-6 mb-0 px-6 py-5 rounded-t-lg">
                <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-white" />
                  </div>
                  Yeni Kullanıcı Ekle
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleInviteUser} className="space-y-6 py-6 px-1">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border-2 border-blue-200">
                  <Label htmlFor="email" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    E-posta <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    required
                    className="bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="user-email-input"
                  />
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
                  <Label htmlFor="name" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <UsersIcon className="w-5 h-5 text-green-600" />
                    Ad Soyad <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={inviteData.name}
                    onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                    required
                    className="bg-white border-2 border-green-300 focus:border-green-500 focus:ring-green-500"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
                  <Label htmlFor="password" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-purple-600" />
                    Şifre <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showInvitePassword ? "text" : "password"}
                      value={inviteData.password}
                      onChange={(e) => setInviteData({ ...inviteData, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="user-password-input"
                      className="pr-10 bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowInvitePassword(!showInvitePassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showInvitePassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-5 rounded-xl border-2 border-orange-200">
                  <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-orange-600" />
                    Rol <span className="text-red-500">*</span>
                  </Label>
                  <Select value={inviteData.role} onValueChange={(value) => {
                    // Role değiştiğinde ilgili alanları temizle
                    setInviteData({ 
                      ...inviteData, 
                      role: value,
                      teamIds: [],
                      projectIds: []
                    });
                  }}>
                    <SelectTrigger data-testid="user-role-select" className="bg-white border-2 border-orange-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="proje_yoneticisi">Proje Yöneticisi</SelectItem>
                      <SelectItem value="takim_lideri">Takım Lideri</SelectItem>
                      <SelectItem value="takim_uyesi">Takım Üyesi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Proje Yöneticisi için proje seçimi */}
                {inviteData.role === 'proje_yoneticisi' && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
                    <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                      <FolderOpen className="w-5 h-5 text-purple-600" />
                      Projeler <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-sm text-gray-500 mb-3">Kullanıcının yöneteceği projeleri seçin (birden fazla seçilebilir)</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-white">
                      {projects.length > 0 ? (
                        projects.map((project) => (
                          <div key={project.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                            <Checkbox
                              id={`invite-project-${project.id}`}
                              checked={inviteData.projectIds?.includes(project.id) || false}
                              onCheckedChange={(checked) => {
                                const newProjectIds = checked
                                  ? [...(inviteData.projectIds || []), project.id]
                                  : (inviteData.projectIds || []).filter(id => id !== project.id);
                                setInviteData({ ...inviteData, projectIds: newProjectIds });
                              }}
                            />
                            <label htmlFor={`invite-project-${project.id}`} className="flex-1 text-sm cursor-pointer">
                              <div className="font-medium text-gray-900">{project.name}</div>
                              <div className="text-xs text-gray-500">{project.description || 'Açıklama yok'}</div>
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">Henüz proje yok</p>
                      )}
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      Seçili: {inviteData.projectIds?.length || 0} proje
                    </p>
                  </div>
                )}

                {/* Takım Lideri ve Takım Üyesi için takım seçimi (Çoklu) */}
                {(inviteData.role === 'takim_lideri' || inviteData.role === 'takim_uyesi') && (
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-5 rounded-xl border-2 border-cyan-200">
                    <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                      <UsersIcon className="w-5 h-5 text-cyan-600" />
                      Takımlar <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-sm text-gray-500 mb-3">Kullanıcının üye olacağı takımları seçin (birden fazla seçilebilir)</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-white">
                      {teams.length > 0 ? (
                        teams.map((team) => (
                          <div key={team.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                            <Checkbox
                              id={`invite-team-${team.id}`}
                              checked={inviteData.teamIds?.includes(team.id) || false}
                              onCheckedChange={(checked) => {
                                const newTeamIds = checked
                                  ? [...(inviteData.teamIds || []), team.id]
                                  : (inviteData.teamIds || []).filter(id => id !== team.id);
                                setInviteData({ ...inviteData, teamIds: newTeamIds });
                              }}
                            />
                            <label htmlFor={`invite-team-${team.id}`} className="flex-1 text-sm cursor-pointer">
                              <div className="font-medium text-gray-900">{team.name}</div>
                              <div className="text-xs text-gray-500">{team.activeMemberCount ?? team.memberIds?.length ?? 0} üye</div>                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">Henüz takım yok</p>
                      )}
                    </div>
                    <p className="text-xs text-cyan-600 mt-2">
                      Seçili: {inviteData.teamIds?.length || 0} takım
                    </p>
                  </div>
                )}

                {/* Admin ve Yorumcu için hiçbir ek alan gösterilmez */}
                
                <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
                  <Button 
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => setShowInviteDialog(false)}
                    className="flex-1 border-2 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4 mr-2" />
                    İptal
                  </Button>
                  <Button 
                    type="submit"
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg" 
                    data-testid="submit-invite-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Kullanıcı Ekle
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kullanıcıyı Sil</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              <strong>{userToDelete?.name}</strong> kullanıcısını silmek istediğinizden emin misiniz?
            </p>
            <p className="text-sm text-gray-500">
              Kullanıcı "Silinen Kullanıcı" olarak işaretlenecek ve giriş yapamayacak. Oluşturduğu içerikler korunacaktır.
            </p>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => {
                setShowDeleteDialog(false);
                setUserToDelete(null);
              }}>
                İptal
              </Button>
              <Button 
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Evet, Sil
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Toplu Kullanıcı Silme</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              <strong>{selectedUsers.length} kullanıcıyı</strong> silmek istediğinizden emin misiniz?
            </p>
            <p className="text-sm text-gray-500">
              Bu kullanıcılar "Silinen Kullanıcı" olarak işaretlenecek ve giriş yapamayacaklar. Oluşturdukları içerikler korunacaktır.
            </p>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>
                İptal
              </Button>
              <Button 
                onClick={confirmBulkDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Evet, {selectedUsers.length} Kullanıcıyı Sil
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      {/* Takım Atamaları modalı kaldırıldı - Kullanıcı Düzenleme modalı aynı işlevleri sunuyor */}

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yetki Yönetimi - {userToEditPermissions?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Kullanıcının sahip olacağı yetkileri seçin. Admin rolü otomatik olarak tüm yetkilere sahiptir.
            </p>
            {userToEditPermissions?.role === 'admin' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Not:</strong> Bu kullanıcı Admin rolünde olduğu için otomatik olarak tüm yetkilere sahiptir.
                </p>
              </div>
            )}
            <div className="space-y-3">
              {AVAILABLE_PERMISSIONS.map((permission) => (
                <div key={permission.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg border border-gray-200">
                  <Checkbox
                    id={`permission-${permission.id}`}
                    checked={selectedPermissions.includes(permission.id)}
                    onCheckedChange={() => togglePermission(permission.id)}
                    disabled={userToEditPermissions?.role === 'admin'}
                  />
                  <label
                    htmlFor={`permission-${permission.id}`}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium text-gray-900">{permission.label}</div>
                    <div className="text-sm text-gray-500 mt-1">{permission.description}</div>
                  </label>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowPermissionsDialog(false);
                  setUserToEditPermissions(null);
                }}
              >
                İptal
              </Button>
              <Button 
                onClick={handleUpdatePermissions}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Yetkileri Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog (Admin Only) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={() => setShowEditDialog(false)}
            className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          >
            <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
          </button>
          
          <DialogHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 -m-6 mb-6 p-6 rounded-t-lg">
            <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Kullanıcı Düzenle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Profil Resmi */}
            <div>
              <Label>Profil Resmi <span className="text-gray-400 font-normal text-xs">(isteğe bağlı)</span></Label>
              <div className="flex items-center gap-4 mt-2">
                {/* Mevcut / Önizleme */}
                <div className="relative w-16 h-16 rounded-full overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center flex-shrink-0">
                  {editPicturePreview ? (
                    <img src={editPicturePreview} alt="önizleme" className="w-full h-full object-cover" />
                  ) : userToEdit?.picture ? (
                    <img src={userToEdit.picture} alt={userToEdit.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-gray-400">
                      {userToEdit?.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="edit-picture-input" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm text-gray-700 transition-colors">
                      <Camera className="w-4 h-4" />
                      {editPictureFile ? 'Değiştir' : 'Resim Seç'}
                    </div>
                    <input
                      id="edit-picture-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (!f) return;
                        if (f.size > 5 * 1024 * 1024) { toast.error('Dosya 5 MB\'dan büyük olamaz'); return; }
                        setEditPictureFile(f);
                        setEditPicturePreview(URL.createObjectURL(f));
                      }}
                    />
                  </label>
                  {editPictureFile && (
                    <button
                      onClick={() => { setEditPictureFile(null); setEditPicturePreview(null); }}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                    >
                      <Trash className="w-3 h-3" /> Seçimi iptal et
                    </button>
                  )}
                  <p className="text-xs text-gray-400">JPG, PNG, WEBP · Maks 5 MB</p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-name">Ad Soyad</Label>
              <Input
                id="edit-name"
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                placeholder="Kullanıcının adı"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({...editData, email: e.target.value})}
                placeholder="kullanici@ornek.com"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-role">Rol</Label>
              <Select value={editData.role} onValueChange={(value) => {
                // Rol değiştiğinde takım seçimlerini sıfırla
                setEditData({
                  ...editData, 
                  role: value,
                  teamIds: [], // Takım üyeliği sıfırla
                  leadTeamIds: [] // Takım liderliği sıfırla
                });
              }}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="proje_yoneticisi">Proje Yöneticisi</SelectItem>
                  <SelectItem value="takim_lideri">Takım Lideri</SelectItem>
                  <SelectItem value="takim_uyesi">Takım Üyesi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Takım Seçimi - Takım Lideri ve Takım Üyesi için */}
            {(editData.role === 'takim_lideri' || editData.role === 'takim_uyesi') && (
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-xl border-2 border-cyan-200">
                <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <UsersIcon className="w-5 h-5 text-cyan-600" />
                  Takımlar
                </Label>
                <p className="text-sm text-gray-500 mb-3">Kullanıcının üye olacağı takımları seçin (birden fazla seçilebilir)</p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-white">
                  {teams.length > 0 ? (
                    teams.map((team) => (
                      <div key={team.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={`edit-team-${team.id}`}
                          checked={editData.teamIds?.includes(team.id) || false}
                          onCheckedChange={(checked) => {
                            const newTeamIds = checked
                              ? [...(editData.teamIds || []), team.id]
                              : (editData.teamIds || []).filter(id => id !== team.id);
                            setEditData({ ...editData, teamIds: newTeamIds });
                          }}
                        />
                        <label htmlFor={`edit-team-${team.id}`} className="flex-1 text-sm cursor-pointer">
                          <div className="font-medium text-gray-900">{team.name}</div>
                          <div className="text-xs text-gray-500">{team.activeMemberCount ?? team.memberIds?.length ?? 0} üye</div>
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">Henüz takım yok</p>
                  )}
                </div>
                <p className="text-xs text-cyan-600 mt-2">
                  Seçili: {editData.teamIds?.length || 0} takım
                </p>
              </div>
            )}
            {editData.role === 'proje_yoneticisi' && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200">
                <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <FolderOpen className="w-5 h-5 text-purple-600" />
                  Projeler
                </Label>
                <p className="text-sm text-gray-500 mb-3">Kullanıcının yöneteceği projeleri seçin (birden fazla seçilebilir)</p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-white">
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <div key={project.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={`edit-project-${project.id}`}
                          checked={editData.projectIds?.includes(project.id) || false}
                          onCheckedChange={(checked) => {
                            const newProjectIds = checked
                              ? [...(editData.projectIds || []), project.id]
                              : (editData.projectIds || []).filter(id => id !== project.id);
                            setEditData({ ...editData, projectIds: newProjectIds });
                          }}
                        />
                        <label htmlFor={`edit-project-${project.id}`} className="flex-1 text-sm cursor-pointer">
                          <div className="font-medium text-gray-900">{project.name}</div>
                          <div className="text-xs text-gray-500">{project.description || 'Açıklama yok'}</div>
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">Henüz proje yok</p>
                  )}
                </div>
                <p className="text-xs text-purple-600 mt-2">
                  Seçili: {editData.projectIds?.length || 0} proje
                </p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleEditUser}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            >
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog (Admin Only) */}
      <Dialog open={showResetPasswordDialog} onOpenChange={(open) => !open && closeResetPasswordDialog()}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={() => closeResetPasswordDialog()}
            className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          >
            <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
          </button>
          
          <DialogHeader className="bg-gradient-to-r from-orange-500 to-red-500 -m-6 mb-6 p-6 rounded-t-lg">
            <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
              <Key className="w-6 h-6" />
              Şifre Sıfırla
            </DialogTitle>
          </DialogHeader>
          
          {!generatedPassword ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>{userToResetPassword?.name}</strong> kullanıcısının şifresini sıfırlamak üzeresiniz.
                </p>
              </div>
              
              {/* Password Mode Selection */}
              <div className="space-y-2">
                <Label>Şifre Yöntemi</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="auto"
                      checked={passwordMode === 'auto'}
                      onChange={() => setPasswordMode('auto')}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">Otomatik Üret</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="manual"
                      checked={passwordMode === 'manual'}
                      onChange={() => setPasswordMode('manual')}
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">Manuel Gir</span>
                  </label>
                </div>
              </div>
              
              {/* Manual Password Input */}
              {passwordMode === 'manual' && (
                <div>
                  <Label htmlFor="manual-password">Yeni Şifre</Label>
                  <Input
                    id="manual-password"
                    type="text"
                    value={manualPassword}
                    onChange={(e) => setManualPassword(e.target.value)}
                    placeholder="En az 8 karakter"
                    minLength={8}
                  />
                  <HelperNote variant="info" className="mt-2">Min. 8 karakter, 1 büyük harf, 1 rakam içermelidir</HelperNote>
                </div>
              )}
              
              {passwordMode === 'auto' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">
                    <RefreshCw className="w-3 h-3 inline mr-1" />
                    12 karakterli güçlü bir şifre otomatik oluşturulacak
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ✅ Şifre başarıyla sıfırlandı!
                </p>
                <p className="text-xs text-green-700">
                  Kullanıcı hesabından otomatik çıkış yapıldı. Yeni şifresi ile giriş yapabilir.
                </p>
              </div>
              
              <div>
                <Label>Yeni Şifre</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="text"
                    value={generatedPassword}
                    readOnly
                    className="font-mono bg-gray-50"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyPasswordToClipboard}
                    title="Kopyala"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <HelperNote variant="warning" className="mt-2">
                  Bu şifreyi kullanıcıya iletmeyi unutmayın!
                </HelperNote>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={closeResetPasswordDialog}
            >
              {generatedPassword ? 'Kapat' : 'İptal'}
            </Button>
            {!generatedPassword && (
              <Button 
                onClick={handleResetPassword}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                disabled={passwordMode === 'manual' && (!manualPassword || manualPassword.length < 6)}
              >
                <Key className="w-4 h-4 mr-2" />
                Şifreyi Sıfırla
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Search and Filter Section */}
      <div className="glass rounded-2xl p-6 mb-6">
        <div className="grid md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <Label htmlFor="search" className="text-sm font-medium text-gray-700 mb-2">
              Ara
            </Label>
            <Input
              id="search"
              type="text"
              placeholder="İsim veya e-posta ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Role Filter */}
          <div>
            <Label htmlFor="filterRole" className="text-sm font-medium text-gray-700 mb-2">
              Rol
            </Label>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger id="filterRole">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Roller</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="proje_yoneticisi">Proje Yöneticisi</SelectItem>
                <SelectItem value="takim_lideri">Takım Lideri</SelectItem>
                <SelectItem value="takim_uyesi">Takım Üyesi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div>
            <Label htmlFor="filterStatus" className="text-sm font-medium text-gray-700 mb-2">
              Durum
            </Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger id="filterStatus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="suspended">Askıya Alındı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Filter Summary and Clear */}
        {(searchTerm || filterRole !== 'all' || filterStatus !== 'all') && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{filteredUsers.length}</span> kullanıcı bulundu
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="text-gray-600 hover:text-gray-800"
            >
              Filtreleri Temizle
            </Button>
          </div>
        )}
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-blue-900">
                {selectedUsers.length} kullanıcı seçildi
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleBulkActivate}
                className="bg-white"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Aktif Et
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleBulkSuspend}
                className="bg-white text-yellow-700 hover:text-yellow-800"
              >
                <Ban className="w-4 h-4 mr-1" />
                Askıya Al
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Sil
              </Button>
            </div>
          </div>
        )}

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {user?.permissions?.includes('can_manage_users') && (
                  <th className="px-6 py-4 text-left">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      aria-label="Tümünü seç"
                    />
                  </th>
                )}
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Kullanıcı / Mail / Durum</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Rol / Tarih</th>
                {user?.permissions?.includes('can_manage_users') && (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">İşlemler</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  {user?.permissions?.includes('can_manage_users') && (
                    <td className="px-6 py-4">
                      {/* Only show checkbox for users that can be deleted */}
                      {canDeleteUser(u) && (
                        <Checkbox
                          checked={selectedUsers.includes(u.id)}
                          onCheckedChange={() => handleSelectUser(u.id)}
                          aria-label={`${u.name} seç`}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      {u.picture ? (
                        <img 
                          src={u.picture.startsWith('http') ? u.picture : `${BACKEND_URL}${u.picture}`}
                          alt={u.name} 
                          className="w-10 h-10 rounded-full object-cover" 
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                          <UsersIcon className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        <p className="font-medium text-gray-900">{u.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${statusColors[u.status || 'active']}`}>
                            {statusLabels[u.status || 'active']}
                          </span>
                          
                          {/* Email doğrulama durumu */}
                          {!u.emailVerified && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              🟡 Email Doğrulama Bekliyor
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${roleColors[u.role]}`}>
                          <Shield className="w-3 h-3 mr-1" />
                          {roleLabels[u.role] || u.role}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        📅 {new Date(u.createdAt).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                  </td>
                  {user?.permissions?.includes('can_manage_users') && (
                    <td className="px-6 py-4">
                      {u.id === user.id ? (
                        <span className="text-xs text-gray-500">Siz</span>
                      ) : (
                        <div className="flex items-center space-x-2">
                          {/* Email doğrulama yeniden gönder butonu */}
                          {!u.emailVerified && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendVerification(u.email)}
                              className="text-orange-600 hover:text-orange-700"
                              title="Doğrulama Maili Tekrar Gönder"
                            >
                              ✉️
                            </Button>
                          )}
                          
                          {/* Admin Only: Edit User */}
                          {user?.role === 'admin' && u.id !== user.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(u)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Kullanıcıyı Düzenle"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Admin Only: Reset Password */}
                          {user?.role === 'admin' && u.id !== user.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openResetPasswordDialog(u)}
                              className="text-orange-600 hover:text-orange-700"
                              title="Şifre Sıfırla"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Admin Only: Permissions Button - Desktop */}
                          {user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPermissionsDialog(u)}
                              className="text-purple-600 hover:text-purple-700"
                              title="Özel Yetkiler"
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Takım Atamaları butonu kaldırıldı - Düzenle butonu aynı işlevi yapıyor */}
                          {u.status === 'suspended' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleActivateUser(u.id)}
                              data-testid={`activate-btn-${u.id}`}
                              className="text-green-600 hover:text-green-700"
                            >
                              <UserCheck className="w-4 h-4 mr-1" />
                              Aktif Et
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSuspendUser(u.id)}
                              data-testid={`suspend-btn-${u.id}`}
                              className="text-yellow-600 hover:text-yellow-700"
                            >
                              <Ban className="w-4 h-4 mr-1" />
                              Askıya Al
                            </Button>
                          )}
                          {/* Delete button - only show if user can be deleted */}
                          {canDeleteUser(u) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setUserToDelete(u);
                                setShowDeleteDialog(true);
                              }}
                              data-testid={`delete-btn-${u.id}`}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-200">
          {filteredUsers.map((u) => (
            <div key={u.id} className="p-4" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start space-x-3 mb-3">
                {u.picture ? (
                  <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                    <UsersIcon className="w-6 h-6 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{u.name}</p>
                  <p className="text-sm text-gray-600 truncate">{u.email}</p>
                  <div className="flex items-center flex-wrap gap-2 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${statusColors[u.status || 'active']}`}>
                      {statusLabels[u.status || 'active']}
                    </span>
                    
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${roleColors[u.role]}`}>
                      <Shield className="w-3 h-3 mr-1" />
                      {roleLabels[u.role] || u.role}
                    </span>
                    
                    {/* Email doğrulama durumu - Mobile */}
                    {!u.emailVerified && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        🟡 Email Doğrulama Bekliyor
                      </span>
                    )}
                    
                    <span className="text-xs text-gray-500">
                      {new Date(u.createdAt).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                {user?.permissions?.includes('can_manage_users') && u.id !== user.id && (
                  <>
                    {/* Admin Only: Edit & Reset Password Buttons - Mobile */}
                    {user?.role === 'admin' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(u)}
                          className="flex-1 text-blue-600"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Düzenle
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openResetPasswordDialog(u)}
                          className="flex-1 text-orange-600"
                        >
                          <Key className="w-4 h-4 mr-2" />
                          Şifre Sıfırla
                        </Button>
                      </div>
                    )}
                    
                    {/* Admin Only: Permissions Button - Mobile */}
                    {user?.role === 'admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPermissionsDialog(u)}
                        className="w-full text-purple-600 hover:text-purple-700"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Özel Yetkiler
                      </Button>
                    )}
                    
                    {/* Email doğrulama yeniden gönder butonu - Mobile */}
                    {!u.emailVerified && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendVerification(u.email)}
                        className="w-full text-orange-600 hover:text-orange-700"
                      >
                        ✉️ Doğrulama Maili Tekrar Gönder
                      </Button>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      {u.status === 'suspended' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActivateUser(u.id)}
                          className="flex-1 text-green-600"
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Aktif Et
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSuspendUser(u.id)}
                          className="flex-1 text-yellow-600"
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          Askıya Al
                        </Button>
                      )}
                      {/* Delete button - only show if user can be deleted */}
                      {canDeleteUser(u) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setUserToDelete(u);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
